import { ResultMessageType } from "@multiweb/types/contracts";
import { MutableTypedObject } from "@multiweb/types/react";
import { setupBem } from "Global/Dom/bem";
import { ErrorHandler } from "UI/Errors";
import { CommentaryFrameDataSourceFullTree } from "UI/UI.Controls/DataSource/CommentaryFrameDataSourceFullTree";
import { AppContext } from "../../../../../App/AppContext";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { IOrderBy, OrderType } from "../../../../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent } from "../../../../../Global/Base/Events";
import { Deferred, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { EventsController } from "../../../../../Global/Dom/EventsController";
import { IValidationError, ValidationErrorType } from "../../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { EOWException, EOWValidateException } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { EventHelper } from "../../../../UI.BaseControls/Helpers/EventHelper";
import { ActionType } from "../../../../UI.Controls/Actions/IAction";
import { CommentaryFrameGrid } from "../../../../UI.Controls/CommentaryFrameGrid";
import { CommentaryFrameDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/CommentaryFrameDataRow";
import { CommentaryFrameDataSource, ICommentaryDataSourceLoadInfo, ICommentaryDataSourceParams, ICommentRowData } from "../../../../UI.Controls/DataSource/CommentaryFrameDataSource";
import { EditableDBDataSource, IActualObjectChangeEventArgs, SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CommentaryFrameAttachment } from "../../Controls/CommentaryFrameAttachment";
import { CommentaryFrameInput } from "../../Controls/CommentaryFrameInput";
import { Control } from "../../Controls/Control";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { ControlUtils } from "../../Controls/ControlUtils";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { UploadManager } from "../../UploadManager";
import { Colors } from "../Colors";
import { ComponentExpander } from "../ComponentExpander";
import { IActualObjectChangedEventArgs } from "../Frames";
import { SharedMethods } from "../SharedMethods";
import { BaseFrame } from "./BaseFrame";
import { IEditableFrame } from "./IEditableFrame";

const bemClass = setupBem("commentary-frame");

/**
 * Třída pro zobrazení frame k zobrazení a přidání komentářů
 */
export class CommentaryFrame extends BaseFrame implements IEditableFrame {
    //#region Events
    public readonly editModeChanged: GlobalEvent<IEditableFrame, boolean> = new GlobalEvent<IEditableFrame, boolean>();
    //#endregion Events

    //#region Fields
    /**
     * Jmeno vazby v objektu na nadrizenem framu
     */
    private _commentaryFrameInput: CommentaryFrameInput;
    private _commentsListContainer: HTMLElement;
    private _componentsElement: HTMLElement;
    private _dataGrid: CommentaryFrameGrid;
    private _dataSource: CommentaryFrameDataSource;
    private _dataLoadingDefferred: Deferred<boolean> = PromiseUtils.defer();
    private _canValueLoadChangedListener: (control: Control, value: boolean) => Promise<void>;
    private _cvlMasterChangeRequest: boolean = false;
    private _orderBy: Array<IOrderBy> = [];
    private _commentaryFrameInputs: Array<CommentaryFrameInput> = [];
    private _authorColor: MutableTypedObject<string> = {};
    private _loggedUserName: string;
    private _componentExpander: ComponentExpander;
    private _eventController: EventsController;

    private _fullTree: boolean = true;
    //#endregion Fields

    //#region Properties
    public get dataGrid(): CommentaryFrameGrid {
        return this._dataGrid;
    }
    public get dataSource(): CommentaryFrameDataSource {
        return this._dataSource;
    }
    public set dataSource(v: CommentaryFrameDataSource) {
        this._dataSource = v;
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlWithLoggerParams) {
        super(params);

        this.element.classList.add(bemClass());
        this._eventController = new EventsController();
        this._initializeComponents();
        this._createFrameCaption();
        this._initilizeCommentaryFrameExpand();
    }
    //#endregion Constructors

    //#region Methods
    private _initilizeCommentaryFrameExpand(): void {
        this._componentExpander = new ComponentExpander(this, null, this._commentsListContainer);
        this._eventController.addListener(this.element, "keyup", (e: KeyboardEvent) => {
            EventHelper.stopPropagation(e);
            this._componentExpander.onKeyClicked.trigger(this._componentExpander, e);
        });
    }

    private _initializeComponents(): void {
        this._setLoggedUserName();
        this._commentaryFrameInput = new CommentaryFrameInput();
        this._commentaryFrameInput.initializeComponents(true, false, () => this._getRequestContext());
        this._commentaryFrameInput.setAvatarNameAndColor(this._loggedUserName, this._authorColor[this._loggedUserName]);
        this._componentsElement = createElement({ classes: bemClass.element("components") });
        this._commentaryFrameInput.fileStartsUploading.add(() => this._setInEditMode(true));
        this._commentaryFrameInput.fileRemovedMessageShowed.add(() => this._setFocusOnExpander());

        //přidá div pro již zadané komentáře
        this._commentsListContainer = createElement({ classes: bemClass.element("list-container") });
        this.container.appendChild(this._commentsListContainer);

        // přidá komponentu pro odesílání textu
        this.container.appendChild(this._commentaryFrameInput);
        this._commentaryFrameInput.appendChild(this._componentsElement);
        this._commentaryFrameInput.sendButtonClicked.add(async (sender, value) => {
            this._commentaryFrameInput.enableButtons(false);
            if (await this._checkUploadsDone() &&
                (!this.masterObject?.isNew || await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })) &&
                ((await this._createNewComment(sender.getAttachmentsList(), value)).length === 0)) {
                await this._checkInEditMode(this._commentaryFrameInput);
            }
            this._commentaryFrameInput.setAttachmentButtonEnabled(true);
            this._setFocusOnExpander();
        });
        this._commentaryFrameInput.rightsToInsertComment = false;
        this._commentaryFrameInput.valueChanged.add(async (_sender, _value) => {
            await this._checkInEditMode(this._commentaryFrameInput);
        });
    }

    private _setFocusOnExpander(): void {
        if (!this._componentExpander.isClosed())
            this._componentExpander.messageWindow.element.focus();
    }

    protected override _creteFrameContainer(): HTMLElement {
        return createElement({ classes: bemClass.element("container") });
    }

    private _setInEditMode(edit: boolean): void {
        this.isEditing = edit;
        this.isChanged = edit;
    }

    private _setLoggedUserName(): void {
        const loggedUserInfo = AppContext.instance.currentUser;
        this._loggedUserName = loggedUserInfo.fullName ? loggedUserInfo.fullName : loggedUserInfo.userName;
        this._setAuthorColor(this._loggedUserName);
    }

    private _setAuthorColor(username: string): void {
        if (!this._authorColor[username])
            this._authorColor[username] = Colors[Object.keys(this._authorColor).length % (Object.keys(Colors).length / 2)];
    }

    private _checkInEditMode(commentaryFrameInput: CommentaryFrameInput): void {
        if (commentaryFrameInput.rightsToInsertComment &&
            (this._commentaryFrameInputs.some((frameInput) => frameInput.commentInputBoxText != "" || frameInput.getAttachmentsList().length > 0) ||
                this._commentaryFrameInput.commentInputBoxText != "" || this._commentaryFrameInput.getAttachmentsList().length > 0))
            this._setInEditMode(true);
        else
            this._setInEditMode(false);
    }

    private _resizeReplyContainer(replyContainer: HTMLElement, commentaryInput: CommentaryFrameInput): void {
        //pri pouziti zoomu v prohlizeci lita vyska commentaryinputu po kazdem znaku +- 1 pixel
        if ((commentaryInput.clientHeight + 1) + "px" == replyContainer.style.height ||
            (commentaryInput.clientHeight - 1) + "px" == replyContainer.style.height ||
            commentaryInput.clientHeight + "px" == replyContainer.style.height)
            return;
        replyContainer.style.height = commentaryInput.clientHeight + "px";
    }

    private async _createNewReply(sender: CommentaryFrameDataRow): Promise<void> {
        if (this._commentaryFrameInputs.some((item) => item.belongsTo == sender.dataKey))
            return;

        const replyContainer = createElement({ classes: bemClass.element("reply-container") });
        const commentaryInput = new CommentaryFrameInput();
        commentaryInput.initializeComponents(this.dataSource.attachmentsEnabled(), true, () => this._getRequestContext());
        commentaryInput.setAvatarNameAndColor(this._loggedUserName, this._authorColor[this._loggedUserName]);
        commentaryInput.attachmentClass = this.dataSource.attachmentclass;
        commentaryInput.attachmentAttribute = this.dataSource.attachmentAttribute;

        commentaryInput.replyTo = this._fullTree ? sender.dataKey : sender.getParentDataKeyOrDataKey();

        commentaryInput.belongsTo = sender.dataKey;
        commentaryInput.rightsToInsertComment = await this._hasRights();
        commentaryInput.clearCommentText();
        commentaryInput.valueChanged.add(() => {
            this._checkInEditMode(commentaryInput);
            this._resizeReplyContainer(replyContainer, commentaryInput);
        });
        commentaryInput.attachmentAdded.add(() => {
            this._resizeReplyContainer(replyContainer, commentaryInput);
        });

        sender.insertFirstChild(replyContainer, false);

        commentaryInput.sendButtonClicked.add(async (sender, value) => {
            commentaryInput.enableButtons(false);
            if (await this._checkUploadsDone() &&
                (await this._createNewComment(sender.getAttachmentsList(), value, commentaryInput.replyTo)).length === 0) {
                commentaryInput.dispose();
                this._removeCommentaryInputReply(replyContainer, commentaryInput);
                await this._checkInEditMode(commentaryInput);
            } else
                commentaryInput.enableButtons(true);
            this._setFocusOnExpander();
        });

        commentaryInput.fileStartsUploading.add(() => this._setInEditMode(true));

        commentaryInput.deleteButtonClicked.add(() => {
            this._removeCommentaryInputReply(replyContainer, commentaryInput);
            this._setFocusOnExpander();
        });
        commentaryInput.fileRemovedMessageShowed.add(() => this._setFocusOnExpander());
        replyContainer.appendChild(commentaryInput);
        this._resizeReplyContainer(replyContainer, commentaryInput);
        this._commentaryFrameInputs.push(commentaryInput);
    }

    private _removeCommentaryInputReply(relativeDiv: HTMLElement, commentaryInput: CommentaryFrameInput): void {
        relativeDiv.remove();
        this._commentaryFrameInputs.splice(this._commentaryFrameInputs.indexOf(commentaryInput, 0), 1);
        commentaryInput.clearAttachmentsList();
        commentaryInput.dispose();
    }

    protected override _createColumns(): Promise<void> {
        return Promise.resolve();
    }

    /**
     * Metoda volana pri zmene mastera
     */
    protected override async _onMasterObjectChanged(_masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void> {
        if (!ControlUtils.isElementVisible(this.element))
            return;

        this._commentaryFrameInput.clearAttachmentsList();

        if (this._cvlMasterChangeRequest) {
            this._cvlMasterChangeRequest = false;
        }

        if (this.actualObject)
            this._setActualObject(null, true);

        await this._updateConditionAndRead(this.dataSource);

        //if (lastActualObject) {
        //    await this.selectRow(lastActualObject.id);
        //}

        this._commentaryFrameInputs.forEach((frameInput) => {
            frameInput.dispose();
            this._removeCommentaryInputReply(frameInput.parentElement, frameInput);
        });

        if (this.masterFrame.actualObject == null || this.masterFrame.actualObject.isNew) {
            if (this.dataGrid)
                this.dataGrid.clearDataRows();
        }
        this._commentaryFrameInput.rightsToInsertComment = await this._hasRights();
        this._commentaryFrameInput.clearCommentText();
    }

    protected _refreshAuthorColor(eventData: ICommentaryDataSourceLoadInfo): void {
        this._authorColor = {};
        this._setAuthorColor(this._loggedUserName);
        const data = eventData.dataGenerator;
        for (const irow of (data() as Iterable<ICommentRowData>)) {
            this._setAuthorColor(irow.data.author);
        }
        this._dataGrid.authorColor = this._authorColor;
    }

    protected override async _initializeFormUI(): Promise<void> {
        // do nothing.
    }
    public override clearProfileInfo(): void {
        // do nothing.
    }
    public override validate(_checkRecommended?: boolean, _checkPostponedRequired?: boolean, _validatedUnchanged?: boolean): Promise<boolean> {
        throw new Error("Method not implemented.");
    }
    public override async validateAndShowMessage(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }
    public override async deleteRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }
    public override editRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }
    public override async newRecord(_objectName?: string): Promise<boolean> {
        throw new Error("Method not implemented.");
    }
    public override prevRow(): void {
        throw new Error("Method not implemented.");
    }
    public override nextRow(): void {
        throw new Error("Method not implemented.");
    }
    public override async createCopy(_propagateError?: boolean): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    private async _checkUploadsDone(): Promise<boolean> {
        if (UploadManager.instance.isUploadRunningOnClass(this.dataSource.attachmentclass)) {
            await MessageWindow.showAlert(new LocalizedStringBuilder("GUI-601245")); //Probíhá upload souborů. Záznam lze uložit až po jeho dokončení.
            return false;
        } else
            return true;
    }

    public override refreshOnClick(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    private _processSort(sort: string): void {
        if (!StringUtils.isNullOrEmpty(sort) && StringUtils.compareCaseInsensitive(sort.substring(0, 3), "asc"))
            this._orderBy = [{ attributeName: this.element.getAttribute("data-datefield"), order: OrderType.ascending }];
        else
            this._orderBy = [{ attributeName: this.element.getAttribute("data-datefield"), order: OrderType.descending }];
    }

    /**
    * Zinicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
    */
    protected override async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;
        this._initializationStarted = true;

        this.condition = this.element.getAttribute("data-condition");
        const condParams = this.element.getAttribute("data-conditionparamsstring");
        if (!StringUtils.isNullOrEmpty(condParams))
            this.conditionParamsAttrs = condParams.split(";");
        this.masterLinkToAttr = this.element.getAttribute("data-masterlinktoattr");
        this.masterAttr = this.element.getAttribute("data-masterattr");
        this._processSort(this.element.getAttribute("data-sort"));

        this._fullTree = DataConverter.toBoolean(this.element.getAttribute("data-fulltree"), true);

        const dataSuppressSaveDialog = this.element.getAttribute("data-suppresssavedialog");
        if (!StringUtils.isNullOrEmpty(dataSuppressSaveDialog))
            this._suppressSaveDialog = DataConverter.toBoolean(dataSuppressSaveDialog);

        this._commentaryFrameInput.attachmentClass = this.element.getAttribute("data-attachmentclass");
        this._commentaryFrameInput.attachmentAttribute = this.element.getAttribute("data-attachmentfields");
        //Aktualizuje práva a následně nastaví property, podle které se (ne)zobrazí komponenta CommentaryFrameInput
        this._commentaryFrameInput.rightsToInsertComment = await this._hasRights();

        try {
            UIManager.instance.showLoader();

            // obecná inicializace frame
            await this._initializeFrame();
            await this._initializeDataSource();
            await this._initializeDataGrid();
            this._isInitialized = true;
            if (!this.dataSource.attachmentsEnabled())
                this._commentaryFrameInput.removeAttachmentButton();
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private async _hasRights(): Promise<boolean> {
        await this.actualizeRights();
        // není možné vkládat komentáře, pokud není vybraný objekt v mastru
        if (this.masterFrame && !this.masterFrame.actualObject)
            return false;
        return this._actualObjectStandardRights.canNew;
    }

    protected async _initializeDataSource(): Promise<void> {
        const dataSourceParams: ICommentaryDataSourceParams = {
            className: this.objectName,
            condition: this.condition,
            orderBy: this._orderBy,
            readVersioned: this.readVersioned,
            maxRecordCount: undefined,
            frameContext: this.frameContext,
        };

        this.dataSource = this._fullTree ? new CommentaryFrameDataSourceFullTree(dataSourceParams) : new CommentaryFrameDataSource(dataSourceParams);

        this.dataSource.attributesChanged.add(async (_senderDataSource: EditableDBDataSource, eventData: IActualObjectChangeEventArgs) => {
            if (this.isEditing)
                this.isChanged = eventData.isChanged;
            //pokud byla data právě dešifrována a týkají se AO, je potřeba přehodnotit práva.
            if (eventData.isDecrypted && this.actualObject && this.actualObject.id == (AttrValueReader.read(eventData.key))) {
                //znovu vyhodnotí práva protože, AO byl právě dešifrován
                await this.actualObject.reload();
                await this.actualizeRights();
                const action = this.menu.getAction(ActionType.standardDecrypt);
                if (action)
                    action.enabled = false;
                await this._triggerRightsChecking();
            }
        });

        const attrs = new Array<string>();
        this.dataSource.authorField = this.element.getAttribute("data-authorfield");
        this.dataSource.dateField = this.element.getAttribute("data-datefield");
        this.dataSource.textField = this.element.getAttribute("data-textfield");
        this.dataSource.hierarchy = this.element.getAttribute("data-hierarchyfield");
        this.dataSource.rightToNew = this._actualObjectStandardRights.canNew;
        this.dataSource.attachmentAttribute = this.element.getAttribute("data-attachmentfields");
        this.dataSource.attachmentclass = this.element.getAttribute("data-attachmentclass");
        this.dataSource.attachmentLinkToAttr = this.element.getAttribute("data-attachmentlinktoattr");
        attrs.push(
            this.dataSource.dateField,
            this.dataSource.textField,
            this.dataSource.hierarchy,
        );

        for (const authorAttr of this.dataSource.authorField.split(";")) {
            if (!StringUtils.isNullOrWhiteSpace(authorAttr))
                attrs.push(authorAttr);
        }

        this.dataSource.setAttrs(attrs);

        this._dataSource.dataLoaded.add((_sender, eventData) => {
            this._refreshAuthorColor(eventData);
        });

        await this.updateCondition(this.dataSource);
    }

    protected async _initializeDataGrid(): Promise<void> {
        const initDataGridFnc = async () => {
            this._dataGrid = new CommentaryFrameGrid();

            if (this._dataGrid.emptyMessage) {
                this._dataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
            }

            if (this._fullTree) this._dataGrid.fetchCount = -1;
            else this._dataGrid.fetchCount = 5;

            this._dataGrid.authorColor = this._authorColor;
            this._dataGrid.objectName = this.objectName;
            this._dataGrid.dataSource = (this.dataSource as CommentaryFrameDataSource);
            this._dataGrid.replyButtonClicked.add(async (sender, _prm) => await this._createNewReply(sender));
            this._commentsListContainer.appendChild(this.dataGrid);

            if (this.masterFrame) {
                this.masterFrame.actualObjectChanged.add(async (_sender, eventArgs) => {
                    this._cvlMasterChangeRequest = true; // nastavení Pomocne promenne pro nenacitani hodnot a prav skrytych elementu
                    if (this.masterFrame)
                        await this.masterFrame.rightsEvaluationTask;
                    this._onMasterObjectChanged(eventArgs);
                });
            } else {
                if (this.ownerForm.queryMode) {
                    this.dataGrid.setToQueryMode(() => this.refreshOnClick());
                } else {
                    await this.dataGrid.loadData();
                }
            }

            this.dataGrid.dataSource.loadingStarted.add(() => {
                UIManager.instance.showLoader();
                this._dataLoadingDefferred = PromiseUtils.defer();
            });

            this.dataGrid.dataSource.dataLoaded.add(() => {
                this._dataLoadingDefferred.resolve(true);
                this.dataLoaded.trigger(this, null);
            });

            this.dataGrid.dataSource.loadingEnded.add(() => {
                UIManager.instance.hideLoader();
            });

            this.dataGrid.dataSource.error.add(() => {
                UIManager.instance.hideLoader();
                this._dataLoadingDefferred.reject();
                this.dataLoadingFailed.trigger(this, null);
            });

            this.dataGrid.dataSource.loadingCancelled.add(() => {
                UIManager.instance.hideLoader();
                this._dataLoadingDefferred.resolve(false);
                this.dataLoadingCancelled.trigger(this, null);
            });

            // pokud už má master frame načtený objekt, tak se o tom vyvolá oznámení
            if (this.masterFrame && this.masterFrame.actualObject != null) {
                this._cvlMasterChangeRequest = true;
                this._onMasterObjectChanged();
            }

            if (this.ownerForm.detailFrame != null)
                this.ownerForm.detailFrame.detailResized.add(() => {
                    this.checkIfIsCompact();
                    this.dataGrid.resizeGrid();
                });

            this.dataGrid.initialized = true;
        };

        if (this.canValueLoad) {
            await initDataGridFnc();
        } else {
            this.canValueLoadChanged.add(async () => {
                await initDataGridFnc();
                if (this.masterObject)
                    this._onMasterObjectChanged();
            }, true);
        }

        //let delayedMasterObjectChangeTimeoutID: number = void 0;
        this.canValueLoadChanged.add(this._canValueLoadChangedListener = async (_control: Control, value: boolean) => {
            if (value) {
                if (!this.dataGrid) {
                    await initDataGridFnc();
                }
                if (this._cvlMasterChangeRequest)
                    this._onMasterObjectChanged();
            }
        });
    }

    private async _processErrors(error: EOWException, data: OWObject): Promise<void> {
        const saveResultData: SaveResultData[] = [];
        if (error instanceof EOWValidateException) {
            for (const ve of error.validationErrors) {
                saveResultData.push({ data: data, messages: [{ text: ve.errorMessage, locKey: ve.attributeNames.join(", "), type: ResultMessageType.error }] });
            }
            await SharedMethods.processSaveResultsAndShowMessages(this, saveResultData);
        } else
            ErrorHandler.instance.handleError(error);
    }

    private async _createNewComment(attachmentsList: CommentaryFrameAttachment[], commentText: string, parentCommentId: string = "", formIsClosing: boolean = false): Promise<IValidationError[]> {
        const errors: IValidationError[] = [];
        if (await this._hasRights()) {
            UIManager.instance.showLoader();
            try {
                if (!StringUtils.isNullOrEmpty(commentText) || attachmentsList.length > 0) {
                    const result = await this.dataSource.createNewComment(this.objectName, commentText, parentCommentId, this.masterLinkToAttr,
                        this.masterFrame?.actualObject?.instanceID);
                    if (result.error) {
                        await this._processErrors(result.error, result.comment);
                        errors.push({ errorMessage: result.error.message, type: ValidationErrorType.dataError });
                    }

                    for (const attachment of attachmentsList) {
                        if (attachment.isValid) {
                            const attachmentResult = await this.dataSource.saveAttachment(result.comment.id, attachment.actualObject);
                            if (attachmentResult.error) {
                                await this._processErrors(attachmentResult.error, attachmentResult.attachment);
                                errors.push({ errorMessage: attachmentResult.error.message, type: ValidationErrorType.dataError });
                            }
                        }
                    }

                    if (errors.length == 0 && !formIsClosing) {
                        const dataRow = await this.dataSource.commentToRowData(result.comment);
                        for (const attachment of attachmentsList) {
                            if (attachment.isValid)
                                dataRow.attachments.push(await this.dataSource.appendAttachment(attachment.actualObject));
                        }
                        this.dataSource.addNewCommentToDataRows(dataRow);
                        if (StringUtils.isNullOrWhiteSpace(parentCommentId)) {
                            this._commentaryFrameInput.clearCommentText();
                            this._commentaryFrameInput.clearAttachmentsList();
                        }
                    }
                }
            } finally {
                UIManager.instance.hideLoader();
            }
        }
        return errors;
    }

    public focus(): void {
        if (this._commentaryFrameInput.commentInputBox.isContentEditable) {
            this._commentaryFrameInput.commentInputBox.focus();
            this._commentaryFrameInput.setCarretAtTheEnd();
        } else if (!this._componentExpander.isClosed())
            this._componentExpander.messageWindow.element.focus();
        else
            this.element.focus();
    }

    public override dispose(): void {
        this._eventController.removeAllListeners();
        this._componentExpander.dispose();
        this.canValueLoadChanged.remove(this._canValueLoadChangedListener);
        this._dataGrid?.dispose();
        this._commentaryFrameInput?.dispose();
        this._commentaryFrameInputs?.forEach(input => input.dispose());
        super.dispose();
    }
    //#endregion Methods
}
